import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Loader2, Mail, ArrowRight, Clock, Building2 } from "lucide-react";
import api from "@/lib/api";

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const token = searchParams.get("token");
  
  const [status, setStatus] = useState("loading"); // loading, success, needs_password, expired, error
  const [message, setMessage] = useState("");
  const [userName, setUserName] = useState("");
  const [passwordSetupToken, setPasswordSetupToken] = useState("");
  
  // Resend verification state
  const [resendEmail, setResendEmail] = useState("");
  const [resending, setResending] = useState(false);
  
  // Ref to prevent double API call in React Strict Mode
  const verificationStarted = useRef(false);

  // If user is already logged in as PROVIDER, redirect to their dashboard
  useEffect(() => {
    if (user && user.role === "PROVIDER") {
      navigate("/provider");
    }
  }, [user, navigate]);

  useEffect(() => {
    // Prevent double call in React Strict Mode using ref
    if (verificationStarted.current) return;
    
    const verify = async () => {
      if (!token) {
        setStatus("error");
        setMessage("No verification token provided.");
        return;
      }
      
      verificationStarted.current = true;
      
      try {
        // Try venue-onboarding endpoint first (for Venue Owners)
        let res;
        try {
          res = await api.get(`/venue-onboarding/verify-email/${token}`);
        } catch (venueErr) {
          // If venue endpoint fails, try the general auth endpoint (for Players/Coaches)
          if (venueErr.response?.status === 400 || venueErr.response?.status === 404) {
            res = await api.get(`/auth/verify-email/${token}`);
          } else {
            throw venueErr;
          }
        }
        
        setUserName(res.data.name);
        
        // Check if user needs to set password
        if (res.data.needs_password && res.data.password_setup_token) {
          setPasswordSetupToken(res.data.password_setup_token);
          setStatus("needs_password");
          setMessage("Email verified! Now please create your password.");
          toast.success("Email verified! Please set your password.");
        } else {
          setStatus("success");
          setMessage(res.data.message);
          toast.success("Email verified successfully!");
        }
      } catch (err) {
        const detail = typeof err.response?.data?.detail === "string"
          ? err.response.data.detail
          : "Verification failed. Please try again or request a new verification email.";
        if (detail.toLowerCase().includes("expired")) {
          setStatus("expired");
        } else if (detail.toLowerCase().includes("already")) {
          setStatus("already_verified");
        } else {
          setStatus("error");
        }
        setMessage(detail);
      }
    };
    
    verify();
  }, [token]);

  const handleResendVerification = async () => {
    if (!resendEmail.trim() || !resendEmail.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }
    
    setResending(true);
    try {
      await api.post("/venue-onboarding/resend-verification", { email: resendEmail });
      toast.success("Verification email sent! Please check your inbox.");
      setStatus("resent");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to resend verification email");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-2">
            <img src="/logo.png" alt="SetPlay" className="h-12 w-12" />
            <h1 className="font-heading text-3xl font-bold uppercase tracking-tight">SetPlay</h1>
          </div>
          <p className="text-muted-foreground text-sm">Venue Owner Verification</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden">
          {/* Status: Loading */}
          {status === "loading" && (
            <div className="p-8 text-center" data-testid="verify-loading">
              <Loader2 className="w-16 h-16 text-court-orange animate-spin mx-auto mb-4" />
              <h2 className="text-xl font-heading font-bold mb-2">Verifying Your Email...</h2>
              <p className="text-muted-foreground">Please wait while we verify your email address.</p>
            </div>
          )}

          {/* Status: Success */}
          {status === "success" && (
            <div className="p-8 text-center" data-testid="verify-success">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="text-xl font-heading font-bold text-green-700 mb-2">Email Verified!</h2>
              <p className="text-muted-foreground mb-4">{message}</p>
              
              {userName && (
                <p className="text-sm text-slate-600 mb-6">
                  Welcome, <span className="font-semibold">{userName}</span>! Your email has been successfully verified.
                </p>
              )}

              {/* Next Steps Info */}
              <div className="bg-court-orange/5 border border-court-orange/20 rounded-lg p-4 mb-6 text-left">
                <div className="flex items-start gap-3">
                  <Building2 className="w-5 h-5 text-court-orange flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-heading font-semibold text-sm uppercase mb-1">Next: Log In to Your Account</p>
                    <p className="text-xs text-muted-foreground">
                      Log in with your email and password to access your Venue Owner Dashboard and start setting up your venue.
                    </p>
                  </div>
                </div>
              </div>

              <Button 
                onClick={() => navigate("/login")}
                className="w-full h-12 bg-court-orange hover:bg-court-orange-hover text-white rounded-full font-heading uppercase tracking-wide shadow-[3px_3px_0px_0px_rgba(0,0,0,1)]"
                data-testid="go-to-login-btn"
              >
                Login
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}

          {/* Status: Needs Password - User registered without password */}
          {status === "needs_password" && (
            <div className="p-8 text-center" data-testid="verify-needs-password">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="text-xl font-heading font-bold text-green-700 mb-2">Email Verified!</h2>
              <p className="text-muted-foreground mb-4">{message}</p>
              
              {userName && (
                <p className="text-sm text-slate-600 mb-6">
                  Welcome, <span className="font-semibold">{userName}</span>! One more step to complete your registration.
                </p>
              )}

              {/* Password Setup Info */}
              <div className="bg-court-green/5 border border-court-green/20 rounded-lg p-4 mb-6 text-left">
                <div className="flex items-start gap-3">
                  <Building2 className="w-5 h-5 text-court-green flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-heading font-semibold text-sm uppercase mb-1">Final Step: Create Your Password</p>
                    <p className="text-xs text-muted-foreground">
                      Set a secure password to protect your Venue Owner account. You'll use this to log in.
                    </p>
                  </div>
                </div>
              </div>

              <Button 
                onClick={() => navigate(`/set-password?token=${passwordSetupToken}`)}
                className="w-full h-12 bg-court-green hover:bg-court-green/90 text-white rounded-full font-heading uppercase tracking-wide shadow-[3px_3px_0px_0px_rgba(0,0,0,1)]"
                data-testid="go-to-set-password-btn"
              >
                Create Password
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}

          {/* Status: Expired */}
          {status === "expired" && (
            <div className="p-8" data-testid="verify-expired">
              <div className="text-center mb-6">
                <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Clock className="w-10 h-10 text-amber-600" />
                </div>
                <h2 className="text-xl font-heading font-bold text-amber-700 mb-2">Link Expired</h2>
                <p className="text-muted-foreground">
                  This verification link has expired (24 hours). Please request a new one below.
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="text-xs font-heading uppercase tracking-wider text-slate-600">Your Email Address</label>
                  <Input 
                    type="email"
                    value={resendEmail}
                    onChange={(e) => setResendEmail(e.target.value)}
                    placeholder="you@email.com"
                    className="h-11 rounded-none border-2 mt-1"
                    data-testid="resend-email-input"
                  />
                </div>
                <Button 
                  onClick={handleResendVerification}
                  disabled={resending}
                  className="w-full h-12 bg-court-orange hover:bg-court-orange-hover text-white rounded-full font-heading uppercase tracking-wide shadow-[3px_3px_0px_0px_rgba(0,0,0,1)]"
                  data-testid="resend-verification-btn"
                >
                  {resending ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Mail className="w-4 h-4 mr-2" />
                      Resend Verification Email
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Status: Already Verified */}
          {status === "already_verified" && (
            <div className="p-8" data-testid="verify-already">
              <div className="text-center mb-6">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle2 className="w-10 h-10 text-green-600" />
                </div>
                <h2 className="text-xl font-heading font-bold text-green-700 mb-2">Already Verified</h2>
                <p className="text-muted-foreground">{message}</p>
              </div>
              <Button 
                onClick={() => navigate("/login")}
                className="w-full h-12 bg-court-orange hover:bg-court-orange-hover text-white rounded-full font-heading uppercase tracking-wide shadow-[3px_3px_0px_0px_rgba(0,0,0,1)]"
                data-testid="go-to-login-btn"
              >
                <ArrowRight className="w-4 h-4 mr-2" />
                Go to Login
              </Button>
            </div>
          )}

          {/* Status: Error */}
          {status === "error" && (
            <div className="p-8" data-testid="verify-error">
              <div className="text-center mb-6">
                <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <XCircle className="w-10 h-10 text-red-600" />
                </div>
                <h2 className="text-xl font-heading font-bold text-red-700 mb-2">Verification Failed</h2>
                <p className="text-muted-foreground">{message}</p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="text-xs font-heading uppercase tracking-wider text-slate-600">Your Email Address</label>
                  <Input 
                    type="email"
                    value={resendEmail}
                    onChange={(e) => setResendEmail(e.target.value)}
                    placeholder="you@email.com"
                    className="h-11 rounded-none border-2 mt-1"
                    data-testid="error-resend-email-input"
                  />
                </div>
                <Button 
                  onClick={handleResendVerification}
                  disabled={resending}
                  className="w-full h-12 bg-court-orange hover:bg-court-orange-hover text-white rounded-full font-heading uppercase tracking-wide shadow-[3px_3px_0px_0px_rgba(0,0,0,1)]"
                  data-testid="error-resend-btn"
                >
                  {resending ? "Sending..." : "Request New Verification Link"}
                </Button>
                
                <Button 
                  variant="ghost"
                  onClick={() => navigate("/auth")}
                  className="w-full text-sm"
                >
                  Back to Login
                </Button>
              </div>
            </div>
          )}

          {/* Status: Resent */}
          {status === "resent" && (
            <div className="p-8 text-center" data-testid="verify-resent">
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Mail className="w-10 h-10 text-blue-600" />
              </div>
              <h2 className="text-xl font-heading font-bold text-blue-700 mb-2">Email Sent!</h2>
              <p className="text-muted-foreground mb-6">
                A new verification link has been sent to your email. Please check your inbox and spam folder.
              </p>
              
              <Button 
                variant="outline"
                onClick={() => navigate("/auth")}
                className="w-full h-11 rounded-none border-2 font-heading uppercase tracking-wide"
              >
                Back to Login
              </Button>
            </div>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-muted-foreground mt-6">
          Need help? Contact us at <a href="mailto:gamena@setplay.net" className="text-court-orange hover:underline">gamena@setplay.net</a>
        </p>
      </div>
    </div>
  );
}
